import { StatisticsModule } from './statistics/statistics.module';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TabViewModule } from 'primeng/tabview';
import { SliderModule } from 'primeng/slider';
import { TreeModule } from 'primeng/tree';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { CalendarModule } from 'primeng/calendar';
import { DividerModule } from 'primeng/divider';
import { BadgeModule } from 'primeng/badge';
import { FileUploadModule } from 'primeng/fileupload';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { MomentModule } from 'ngx-moment';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import * as Sentry from "@sentry/angular-ivy";
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { ScrollPanelModule } from 'primeng/scrollpanel';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { AuthInterceptor } from './services/auth.interceptor';

import { CoreModule } from 'projects/core/src/lib/core.module';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { AdminComponent } from './pages/admin/admin.component';
import { UserListComponent } from './pages/admin/administration/users/user-list/user-list.component';
import { TopbarComponent } from './partials/layout/topbar/topbar.component';
import { FooterComponent } from './partials/layout/footer/footer.component';
import { MenuComponent } from './partials/layout/menu/menu.component';
import { SearchComponent } from './partials/layout/search/search.component';
import { RightmenuComponent } from './partials/layout/rightmenu/rightmenu.component';
import { MenuitemComponent } from './partials/layout/menuitem/menuitem.component';
import { AcListComponent } from './pages/admin/acs/ac-list/ac-list.component';
import { TemplateListComponent } from './pages/admin/basedata/templates/template-list/template-list.component';
import { RatingtypeListComponent } from './pages/admin/basedata/ratingtypes/ratingtype-list/ratingtype-list.component';
import { CompetenceListComponent } from './pages/admin/basedata/competences/competence-list/competence-list.component';
import { ExerciseListComponent } from './pages/admin/basedata/exercises/exercise-list/exercise-list.component';
import { ExerciseDetailComponent } from './pages/admin/basedata/exercises/exercise-detail/exercise-detail.component';
import { ExerciseBasedataComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-basedata/exercise-basedata.component';
import { ExerciseCompetencesComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-competences/exercise-competences.component';
import { ExerciseElementsComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-elements/exercise-elements.component';
import { ExerciseVersionsComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-versions/exercise-versions.component';
import { ElementListComponent } from './pages/admin/basedata/elements/element-list/element-list.component';
import { ElementDetailComponent } from './pages/admin/basedata/elements/element-detail/element-detail.component';
import { ElementBasedataComponent } from './pages/admin/basedata/elements/element-detail/partials/element-basedata/element-basedata.component';
import { ElementContentComponent } from './pages/admin/basedata/elements/element-detail/partials/element-content/element-content.component';
import { ElementNewComponent } from './pages/admin/basedata/elements/element-detail/partials/element-new/element-new.component';
import { TemplateDetailComponent } from './pages/admin/basedata/templates/template-detail/template-detail.component';
import { TemplateBasedataComponent } from './pages/admin/basedata/templates/template-detail/partials/template-basedata/template-basedata.component';
import { AcDetailComponent } from './pages/admin/acs/ac-detail/ac-detail.component';
import { AcBasedataComponent } from './pages/admin/acs/ac-detail/partials/ac-basedata/ac-basedata.component';
import { AcPersonsComponent } from './pages/admin/acs/ac-detail/partials/ac-persons/ac-persons.component';
import { AcTimingComponent } from './pages/admin/acs/ac-detail/partials/ac-timing/ac-timing.component';
import { UserDetailComponent } from './pages/admin/administration/users/user-detail/user-detail.component';
import { RatingtypeDetailComponent } from './pages/admin/basedata/ratingtypes/ratingtype-detail/ratingtype-detail.component';
import { CompetenceDetailComponent } from './pages/admin/basedata/competences/competence-detail/competence-detail.component';
import { CompetenceNewComponent } from './pages/admin/basedata/competences/competence-detail/partials/competence-new/competence-new.component';
import { CompetenceBasedataComponent } from './pages/admin/basedata/competences/competence-detail/partials/competence-basedata/competence-basedata.component';
import { ExercisePartsComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-parts/exercise-parts.component';
import { ElementVisibilityComponent } from './pages/admin/basedata/elements/element-detail/partials/element-visibility/element-visibility.component';
import { AcExercisesComponent } from './pages/admin/acs/ac-detail/partials/ac-exercises/ac-exercises.component';
import { UserBasedataComponent } from './pages/admin/administration/users/user-detail/partials/user-basedata/user-basedata.component';
import { UserNewComponent } from './pages/admin/administration/users/user-detail/partials/user-new/user-new.component';
import { RatingtypeBasedataComponent } from './pages/admin/basedata/ratingtypes/ratingtype-detail/partials/ratingtype-basedata/ratingtype-basedata.component';
import { RatingtypeNewComponent } from './pages/admin/basedata/ratingtypes/ratingtype-detail/partials/ratingtype-new/ratingtype-new.component';
import { TemplateNewComponent } from './pages/admin/basedata/templates/template-detail/partials/template-new/template-new.component';
import { AcNewComponent } from './pages/admin/acs/ac-detail/partials/ac-new/ac-new.component';
import { AcFilesComponent } from './pages/admin/acs/ac-detail/partials/ac-files/ac-files.component';
import { RecommendationListComponent } from './pages/admin/basedata/recommendations/recommendation-list/recommendation-list.component';
import { RecommendationDetailComponent } from './pages/admin/basedata/recommendations/recommendation-detail/recommendation-detail.component';
import { RecommendationBasedataComponent } from './pages/admin/basedata/recommendations/recommendation-detail/partials/recommendation-basedata/recommendation-basedata.component';
import { RecommendationNewComponent } from './pages/admin/basedata/recommendations/recommendation-detail/partials/recommendation-new/recommendation-new.component';
import { TemplateOnlinetestsComponent } from './pages/admin/basedata/templates/template-detail/partials/template-onlinetests/template-onlinetests.component';
import { AcOnlinetestsComponent } from './pages/admin/acs/ac-detail/partials/ac-onlinetests/ac-onlinetests.component';
import { PartnerListComponent } from './pages/admin/partners/partner-list/partner-list.component';
import { PartnerDetailComponent } from './pages/admin/partners/partner-detail/partner-detail.component';
import { PartnerBasedataComponent } from './pages/admin/partners/partner-detail/partials/partner-basedata/partner-basedata.component';
import { OnlinetestDetailComponent } from './pages/admin/onlinetests/onlinetest-detail/onlinetest-detail.component';
import { OnlinetestBasedataComponent } from './pages/admin/onlinetests/onlinetest-detail/partials/onlinetest-basedata/onlinetest-basedata.component';
import { PasswordEnterEmailComponent } from './pages/password/password-enter-email/password-enter-email.component';
import { PasswordRenewPasswordComponent } from './pages/password/password-renew-password/password-renew-password.component';
import { AcCompetencesComponent } from './partials/data/ac-competences/ac-competences.component';
import { BusinessunitListComponent } from './pages/admin/basedata/businessunits/businessunit-list/businessunit-list.component';
import { ToolboxCandidateListComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-list/toolbox-candidate-list.component';
import { ToolboxCandidateDetailComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-detail/toolbox-candidate-detail.component';
import { ToolboxCandidateBasedataComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-detail/partials/toolbox-candidate-basedata/toolbox-candidate-basedata.component';
import { ToolboxCandidateNewComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-detail/partials/toolbox-candidate-new/toolbox-candidate-new.component';
import { ToolboxCandidateCredentialsComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-detail/partials/toolbox-candidate-credentials/toolbox-candidate-credentials.component';
import { ToolboxCandidateResultsComponent } from './pages/admin/toolbox-candidates/toolbox-candidate-detail/partials/toolbox-candidate-results/toolbox-candidate-results.component';
import { CountryTimezoneComponent } from './partials/form/country-timezone/country-timezone.component';
import { SharedModule } from './shared/shared.module';
import { TagModule } from 'primeng/tag';
import { RightsComponent } from './pages/admin/administration/rights/rights.component';
import { WithRightDirective } from './directives/with-right.directive';
import { HoverOnRightDirective } from './directives/hover-on-right.directive';
import { ExerciseNewComponent } from './pages/admin/basedata/exercises/exercise-detail/partials/exercise-new/exercise-new.component';
import { RoomBasedataComponent } from './pages/admin/basedata/rooms/room-detail/partials/room-basedata/room-basedata.component';
import { RoomDetailComponent } from './pages/admin/basedata/rooms/room-detail/room-detail.component';
import { RoomListComponent } from './pages/admin/basedata/rooms/room-list/room-list.component';
import { TeamListComponent } from './pages/admin/basedata/teams/team-list/team-list.component';
import { TeamDetailComponent } from './pages/admin/basedata/teams/team-detail/team-detail.component';
import { TeamBasedataComponent } from './pages/admin/basedata/teams/team-detail/partials/team-basedata/team-basedata.component';
import { LocationListComponent } from './pages/admin/basedata/locations/location-list/location-list.component';
import { LocationDetailComponent } from './pages/admin/basedata/locations/location-detail/location-detail.component';
import { LanguageListComponent } from './pages/admin/basedata/languages/language-list/language-list.component';
import { LanguageDetailComponent } from './pages/admin/basedata/languages/language-detail/language-detail.component';
import { LanguageBasedataComponent } from './pages/admin/basedata/languages/language-detail/partials/language-basedata/language-basedata.component';
import { LocationBasedataComponent } from './pages/admin/basedata/locations/location-detail/partials/location-basedata/location-basedata.component';
import { TeamNewComponent } from './pages/admin/basedata/teams/team-detail/partials/team-new/team-new.component';
import { LocationNewComponent } from './pages/admin/basedata/locations/location-detail/partials/location-new/location-new.component';
import { RoomNewComponent } from './pages/admin/basedata/rooms/room-detail/partials/room-new/room-new.component';
import { DocumentsListComponent } from './pages/admin/contents/documents/documents-list/documents-list.component';
import { FileHandlingComponent } from './partials/data/file-handling/file-handling.component';
import { UserLocationsComponent } from './pages/admin/administration/users/user-detail/partials/user-locations/user-locations.component';
import { UserTemplatesComponent } from './pages/admin/administration/users/user-detail/partials/user-templates/user-templates.component';
import { TexttemplatesListComponent } from './pages/admin/contents/texttemplates/texttemplate-list/texttemplate-list.component';
import { TextblocksListComponent } from './pages/admin/contents/textblocks/textblock-list/textblock-list.component';
import { TextblockDetailComponent } from './pages/admin/contents/textblocks/textblock-detail/textblock-detail.component';
import { TextblockBasedataComponent } from './pages/admin/contents/textblocks/textblock-detail/partials/textblock-basedata/textblock-basedata.component';
import { TextblockNewComponent } from './pages/admin/contents/textblocks/textblock-detail/partials/textblock-new/textblock-new.component';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PlaceholdersComponent } from './partials/data/placeholders/placeholders.component';
import { LanguageNewComponent } from './pages/admin/basedata/languages/language-detail/partials/language-new/language-new.component';
import { ContentEditorComponent } from './partials/data/content-editor/content-editor.component';
import { TexttemplateDetailComponent } from './pages/admin/contents/texttemplates/texttemplate-detail/texttemplate-detail.component';
import { TexttemplateBasedataComponent } from './pages/admin/contents/texttemplates/texttemplate-detail/partials/texttemplate-basedata/texttemplate-basedata.component';
import { SentmessagesListComponent } from './pages/admin/contents/sentmessages/sentmessage-list/sentmessage-list.component';
import { AppointmentListComponent } from './partials/data/appointments/appointment-list/appointment-list.component';
import { ObserverListComponent } from './partials/data/appointments/observer-list/observer-list.component';
import { AppointmentCalendarComponent } from './partials/data/appointments/appointment-calendar/appointment-calendar.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MyProfileComponent } from './pages/admin/profile/my-profile/my-profile.component';
import { MyAvailabilitiesComponent } from './pages/admin/profile/my-availabilities/my-availabilities.component';
import { AppointmentsComponent } from './pages/admin/basedata/appointments/appointments.component';
import { SettingsTemplatesComponent } from './pages/admin/planning/settings-templates/settings-templates.component';
import { SettingsUnitsComponent } from './pages/admin/planning/settings-units/settings-units.component';
import { OrderListComponent } from './pages/admin/planning/orders/order-list/order-list.component';
import { OrderDetailComponent } from './pages/admin/planning/orders/order-detail/order-detail.component';
import { TexttemplateFilesComponent } from './pages/admin/contents/texttemplates/texttemplate-detail/partials/texttemplate-files/texttemplate-files.component';
import { OrdersModule } from './orders/orders.module';
import { OrderDetailJobinformationComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-jobinformation/order-detail-jobinformation.component';
import { OrderDetailOrderdataComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-orderdata/order-detail-orderdata.component';
import { OrderDetailParticipantsComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-participants/order-detail-participants.component';
import { OrderDetailDocumentsComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-documents/order-detail-documents.component';
import { OrderDetailCandidatesComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-candidates/order-detail-candidates.component';
import { ProfileBasedataComponent } from './pages/admin/profile/my-profile/partials/profile-basedata/profile-basedata.component';
import { PrivacyComponent } from './partials/layout/privacy/privacy.component';
import { OrderDetailBasedataComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-basedata/order-detail-basedata.component';
import { OrderDetailLogComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-log/order-detail-log.component';
import { ResourcesListComponent } from './pages/admin/planning/resources-list/resources-list.component';
import { ReportingsModule } from './reportings/reportings.module';
import { MessageordersListComponent } from './pages/admin/administration/messageorders/messageorder-list/messageorder-list.component';
import { MessageorderDetailComponent } from './pages/admin/administration/messageorders/messageorder-detail/messageorder-detail.component';
import { MessageorderBasedataComponent } from './pages/admin/administration/messageorders/messageorder-detail/partials/messageorder-basedata/messageorder-basedata.component';
import { MessageorderNewComponent } from './pages/admin/administration/messageorders/messageorder-detail/partials/messageorder-new/messageorder-new.component';
import { MessageorderSettingsComponent } from './pages/admin/administration/messageorders/messageorder-detail/partials/messageorder-settings/messageorder-settings.component';
import { AcPersonsCandidatesComponent } from './pages/admin/acs/ac-detail/partials/ac-persons/ac-persons-candidates/ac-persons-candidates.component';
import { AcPersonsObserversComponent } from './pages/admin/acs/ac-detail/partials/ac-persons/ac-persons-observers/ac-persons-observers.component';
import { InheritDialogComponent } from './partials/data/inherit-dialog/inherit-dialog.component';
import { AcMoveSelectorComponent } from './partials/data/ac-move-selector/ac-move-selector.component';
import { MoveOrderComponent } from './pages/admin/planning/orders/move-order/move-order.component';
import { MoveOrderDataComponent } from './pages/admin/planning/orders/move-order/partials/move-order-data/move-order-data.component';
import { MoveOrderSuggestionsComponent } from './pages/admin/planning/orders/move-order/partials/move-order-suggestions/move-order-suggestions.component';
import { ResourcesFiltersComponent } from './pages/admin/planning/resources-list/partials/resources-filters/resources-filters.component';
import { ResourcesChartComponent } from './pages/admin/planning/resources-list/partials/resources-chart/resources-chart.component';
import { ResourcesDataComponent } from './pages/admin/planning/resources-list/partials/resources-data/resources-data.component';
import { CustomerListComponent } from './pages/admin/basedata/customers/customer-list/customer-list.component';
import { CustomerDetailComponent } from './pages/admin/basedata/customers/customer-detail/customer-detail.component';
import { CustomerBasedataComponent } from './pages/admin/basedata/customers/customer-detail/partials/customer-basedata/customer-basedata.component';
import { CustomerNewComponent } from './pages/admin/basedata/customers/customer-detail/partials/customer-new/customer-new.component';
import { WithFeatureDirective } from './directives/with-feature.directive';
import { BillingListComponent } from './pages/admin/administration/billing/billing-list/billing-list.component';
import { BillingBasedataComponent } from './pages/admin/administration/billing/billing-detail/partials/billing-basedata/billing-basedata.component';
import { BillingDetailComponent } from './pages/admin/administration/billing/billing-detail/billing-detail.component';
import { OrderDetailFinishDialogComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-finish-dialog/order-detail-finish-dialog.component';
import { TemplateTodosComponent } from './pages/admin/basedata/templates/template-detail/partials/template-todos/template-todos.component';
import { OrderDetailTodosComponent } from './pages/admin/planning/orders/order-detail/partials/order-detail-todos/order-detail-todos.component';
import { RoleListComponent } from './pages/admin/administration/roles/role-list/role-list.component';
import { RoleDetailComponent } from './pages/admin/administration/roles/role-detail/role-detail.component';
import { RoleNewComponent } from './pages/admin/administration/roles/role-detail/partials/role-new/role-new.component';
import { RoleBasedataComponent } from './pages/admin/administration/roles/role-detail/partials/role-basedata/role-basedata.component';
import { RoleDataScopesComponent } from './pages/admin/administration/roles/role-detail/partials/role-data-scopes/role-data-scopes.component';
import { DataScopeListComponent } from './pages/admin/administration/data-scopes/data-scope-list/data-scope-list.component';
import { DataScopeDetailComponent } from './pages/admin/administration/data-scopes/data-scope-detail/data-scope-detail.component';
import { DataScopeBasedataComponent } from './pages/admin/administration/data-scopes/data-scope-detail/data-scope-basedata/data-scope-basedata.component';
import { DataScopeNewComponent } from './pages/admin/administration/data-scopes/data-scope-detail/data-scope-new/data-scope-new.component';
import { DataScopeTemplatesComponent } from './pages/admin/administration/data-scopes/data-scope-detail/data-scope-templates/data-scope-templates.component';
import { DataScopeBusinessunitsComponent } from './pages/admin/administration/data-scopes/data-scope-detail/data-scope-businessunits/data-scope-businessunits.component';
import { OnlinetestCompetencesComponent } from './pages/admin/onlinetests/onlinetest-detail/partials/onlinetest-competences/onlinetest-competences.component';
import { ReporttemplatesListComponent } from './pages/admin/contents/reporttemplates/reporttemplates-list/reporttemplates-list.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    DashboardComponent,
    AdminComponent,
    UserListComponent,
    RoleListComponent,
    TopbarComponent,
    FooterComponent,
    MenuComponent,
    SearchComponent,
    RightmenuComponent,
    MenuitemComponent,
    AcListComponent,
    TemplateListComponent,
    RatingtypeListComponent,
    CompetenceListComponent,
    ExerciseListComponent,
    ExerciseDetailComponent,
    ExerciseBasedataComponent,
    ExerciseCompetencesComponent,
    ExerciseElementsComponent,
    ExerciseVersionsComponent,
    ExerciseNewComponent,
    ElementListComponent,
    ElementDetailComponent,
    ElementBasedataComponent,
    ElementContentComponent,
    ElementNewComponent,
    TemplateDetailComponent,
    TemplateBasedataComponent,
    AcDetailComponent,
    AcBasedataComponent,
    AcPersonsComponent,
    AcPersonsCandidatesComponent,
    AcPersonsObserversComponent,
    AcTimingComponent,
    UserDetailComponent,
    RoleDetailComponent,
    RatingtypeDetailComponent,
    CompetenceDetailComponent,
    CompetenceNewComponent,
    CompetenceBasedataComponent,
    ExercisePartsComponent,
    ElementVisibilityComponent,
    AcExercisesComponent,
    UserBasedataComponent,
    UserLocationsComponent,
    UserTemplatesComponent,
    UserNewComponent,
    RoleBasedataComponent,
    RoleNewComponent,
    RoleDataScopesComponent,
    DataScopeListComponent,
    DataScopeDetailComponent,
    DataScopeBasedataComponent,
    DataScopeNewComponent,
    DataScopeTemplatesComponent,
    DataScopeBusinessunitsComponent,
    RatingtypeBasedataComponent,
    RatingtypeNewComponent,
    TemplateNewComponent,
    AcNewComponent,
    AcFilesComponent,
    RecommendationListComponent,
    RecommendationDetailComponent,
    RecommendationBasedataComponent,
    RecommendationNewComponent,
    MyProfileComponent,
    TemplateOnlinetestsComponent,
    TemplateTodosComponent,
    AcOnlinetestsComponent,
    PartnerListComponent,
    PartnerBasedataComponent,
    PartnerDetailComponent,
    OnlinetestDetailComponent,
    OnlinetestBasedataComponent,
    OnlinetestCompetencesComponent,
    PasswordEnterEmailComponent,
    PasswordRenewPasswordComponent,
    AcCompetencesComponent,
    BusinessunitListComponent,
    ToolboxCandidateListComponent,
    ToolboxCandidateDetailComponent,
    ToolboxCandidateBasedataComponent,
    ToolboxCandidateNewComponent,
    ToolboxCandidateCredentialsComponent,
    ToolboxCandidateResultsComponent,
    CountryTimezoneComponent,
    RightsComponent,
    WithRightDirective,
    WithFeatureDirective,
    HoverOnRightDirective,
    RoomListComponent,
    RoomDetailComponent,
    RoomBasedataComponent,
    RoomNewComponent,
    LocationListComponent,
    LocationDetailComponent,
    LocationBasedataComponent,
    LocationNewComponent,
    TeamListComponent,
    TeamDetailComponent,
    TeamBasedataComponent,
    TeamNewComponent,
    LanguageListComponent,
    LanguageDetailComponent,
    LanguageBasedataComponent,
    LanguageNewComponent,
    DocumentsListComponent,
    ReporttemplatesListComponent,
    TexttemplatesListComponent,
    TexttemplateDetailComponent,
    TexttemplateBasedataComponent,
    TexttemplateFilesComponent,
    TextblocksListComponent,
    TextblockDetailComponent,
    TextblockNewComponent,
    TextblockBasedataComponent,
    FileHandlingComponent,
    PlaceholdersComponent,
    ContentEditorComponent,
    SentmessagesListComponent,
    AppointmentsComponent,
    AppointmentListComponent,
    ObserverListComponent,
    AppointmentCalendarComponent,
    MyAvailabilitiesComponent,
    SettingsTemplatesComponent,
    SettingsUnitsComponent,
    OrderListComponent,
    OrderDetailComponent,
    OrderDetailJobinformationComponent,
    OrderDetailOrderdataComponent,
    OrderDetailParticipantsComponent,
    OrderDetailDocumentsComponent,
    OrderDetailCandidatesComponent,
    ProfileBasedataComponent,
    PrivacyComponent,
    OrderDetailBasedataComponent,
    OrderDetailLogComponent,
    ResourcesListComponent,
    MessageordersListComponent,
    MessageorderDetailComponent,
    MessageorderBasedataComponent,
    MessageorderNewComponent,
    MessageorderSettingsComponent,
    InheritDialogComponent,
    AcMoveSelectorComponent,
    MoveOrderComponent,
    MoveOrderDataComponent,
    MoveOrderSuggestionsComponent,
    ResourcesFiltersComponent,
    ResourcesChartComponent,
    ResourcesDataComponent,
    CustomerListComponent,
    CustomerDetailComponent,
    CustomerBasedataComponent,
    CustomerNewComponent,
    BillingListComponent,
    BillingBasedataComponent,
    BillingDetailComponent,
    OrderDetailFinishDialogComponent,
    OrderDetailTodosComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    OrdersModule,
    ReportingsModule,
    StatisticsModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    MultiSelectModule,
    PasswordModule,
    ProgressSpinnerModule,
    MenuModule,
    TableModule,
    DialogModule,
    ConfirmDialogModule,
    DropdownModule,
    ToastModule,
    MessagesModule,
    MessageModule,
    TooltipModule,
    TabViewModule,
    SliderModule,
    TreeModule,
    EditorModule,
    CheckboxModule,
    InputNumberModule,
    CalendarModule,
    DividerModule,
    BadgeModule,
    FileUploadModule,
    CascadeSelectModule,
    SelectButtonModule,
    InputSwitchModule,
    TagModule,
    ScrollPanelModule,
    MomentModule,
    TranslocoRootModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ConfirmationService
    },
    {
      provide: MessageService
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'tinymce/tinymce.min.js'
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
